import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from './Header';
import { Footer } from './Footer';
import { Toaster } from '../ui/sonner';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X } from 'lucide-react';
import { CookieConsent } from '../CookieConsent';
import { toast } from 'sonner';

// Generate or get session ID for analytics
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('rani_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('rani_session_id', sessionId);
  }
  return sessionId;
};

export const Layout = ({ children, seo }) => {
  const { settings, getLocalizedText, API, language } = useApp();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [popupEmail, setPopupEmail] = useState('');
  const [submittingEmail, setSubmittingEmail] = useState(false);

  const popupTexts = {
    ro: {
      placeholder: 'Adresa ta de email',
      subscribe: 'Aboneaza-te',
      thanks: 'Multumim pentru abonare!'
    },
    en: {
      placeholder: 'Your email address',
      subscribe: 'Subscribe',
      thanks: 'Thanks for subscribing!'
    }
  };
  const pt = popupTexts[language] || popupTexts.ro;

  // Analytics tracking - track page views
  useEffect(() => {
    const trackPageView = async () => {
      try {
        await fetch(`${API}/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            session_id: getSessionId(),
          })
        });
      } catch (e) {
        // Silent fail for analytics
      }
    };
    trackPageView();
  }, [location.pathname, API]);

  // Handle timed popup
  useEffect(() => {
    if (!settings?.popup_enabled || popupDismissed) return;
    
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('rani_popup_shown');
    if (popupShown) return;

    const delay = (settings.popup_delay_seconds || 10) * 1000;
    const timer = setTimeout(() => {
      setShowPopup(true);
      sessionStorage.setItem('rani_popup_shown', 'true');
    }, delay);

    return () => clearTimeout(timer);
  }, [settings?.popup_enabled, settings?.popup_delay_seconds, popupDismissed]);

  const dismissPopup = () => {
    setShowPopup(false);
    setPopupDismissed(true);
  };

  const handlePopupEmailSubmit = async (e) => {
    e.preventDefault();
    if (!popupEmail || !popupEmail.includes('@')) return;
    
    setSubmittingEmail(true);
    try {
      await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: popupEmail, source: 'popup' })
      });
      toast.success(pt.thanks);
      dismissPopup();
    } catch (err) {
      // Still dismiss even if API fails
      toast.success(pt.thanks);
      dismissPopup();
    } finally {
      setSubmittingEmail(false);
    }
  };

  // Inject Google Analytics script
  useEffect(() => {
    if (!settings?.google_analytics_id) return;

    const gaId = settings.google_analytics_id;
    
    // Check if already loaded
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) return;

    // Load gtag.js
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script1);

    // Initialize gtag
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `;
    document.head.appendChild(script2);

    return () => {
      // Cleanup if needed (optional)
    };
  }, [settings?.google_analytics_id]);

  // Inject Meta Pixel script
  useEffect(() => {
    if (!settings?.meta_pixel_id) return;

    const pixelId = settings.meta_pixel_id;
    
    // Check if already loaded
    if (window.fbq) return;

    // Meta Pixel base code
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(noscript);
  }, [settings?.meta_pixel_id]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* SEO Meta Tags */}
      {seo && (
        <Helmet>
          {seo.title && <title>{seo.title}</title>}
          {seo.description && <meta name="description" content={seo.description} />}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
          {seo.title && <meta property="og:title" content={seo.title} />}
          {seo.description && <meta property="og:description" content={seo.description} />}
        </Helmet>
      )}
      
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <Toaster position="bottom-right" />
      <CookieConsent />

      {/* Timed Popup Modal with Email Collection */}
      {showPopup && settings?.popup_enabled && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={dismissPopup}
          data-testid="popup-overlay"
        >
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md mx-4 p-8 animate-in fade-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
            data-testid="popup-modal"
          >
            <button
              onClick={dismissPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="popup-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="text-center">
              <h3 className="font-heading text-2xl font-bold text-[#1A1625] mb-3">
                {getLocalizedText(settings.popup_title) || 'Oferta Speciala!'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {getLocalizedText(settings.popup_content) || 'Aboneaza-te pentru oferte exclusive!'}
              </p>
              
              {/* Email Collection Form */}
              <form onSubmit={handlePopupEmailSubmit} className="space-y-3 mb-4">
                <Input
                  type="email"
                  placeholder={pt.placeholder}
                  value={popupEmail}
                  onChange={(e) => setPopupEmail(e.target.value)}
                  className="text-center"
                  required
                />
                <Button
                  type="submit"
                  disabled={submittingEmail}
                  className="w-full bg-primary hover:bg-purple-600 text-white rounded-full"
                >
                  {submittingEmail ? '...' : pt.subscribe}
                </Button>
              </form>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissPopup}
                className="text-muted-foreground text-sm"
              >
                <Link to={settings.popup_button_link || '/shop'}>
                  {getLocalizedText(settings.popup_button_text) || 'Vezi Ofertele'}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
